import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ProductGallery } from '../components/product/ProductGallery';
import { ProductAssetSlot } from '../components/product/ProductAssetSlot';
import { WarrantyTag } from '../components/ui/WarrantyTag';
import { ProductRating } from '../components/reviews/ProductRating';
import { PendingNote } from '../components/ui/PendingNote';
import { m1ProSpecs } from '../data/specs';
import { pricingConfig } from '../data/pricing';
import { useLanguage } from '../contexts/LanguageContext';
import { formatMoney, priceBundle } from '../utils/pricing';
import { buttonClasses } from '../utils/buttonStyles';
import { track } from '../utils/analytics';
import { packagingContents, packagingDesign } from '../data/packaging';
const highlights = [{
  title: 'Wireless',
  body: 'A self-contained light. No cable runs to the water.'
}, {
  title: 'Rechargeable',
  body: 'Internal battery with USB-C charging behind a blue protective cover.'
}, {
  title: 'Magnetic',
  body: 'Seats onto its matched-diameter Magnetic Mount System base.'
}, {
  title: 'RGB',
  body: 'Color options from deep electric blue to full-spectrum.'
}];
export function M1Pro() {
  const {
    lang
  } = useLanguage();
  const minPer = Math.min(...pricingConfig.bundles.map(b => priceBundle(b).perUnit));
  useEffect(() => {
    track('view_content', {
      content_name: 'M1 PRO',
      content_ids: ['MFL-M1PRO-1'],
      content_type: 'product',
      value: pricingConfig.unitPrice,
      currency: pricingConfig.currency
    });
  }, []);
  return <>
      <section className="mx-auto grid max-w-site gap-12 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1.1fr_1fr] lg:gap-20" data-id="element-1149" data-mp="20m9n29h4x0kn">
        <ProductGallery data-id="element-1150" data-mp="1vn59l6n6ggoi" />
        <div className="lg:pt-6" data-id="element-1151" data-mp="1vlvdgmzvxiki">
          <ProductRating size="md" className="mb-4" data-id="element-1152" data-mp="3c1ohp6lvfgcl" />
          <p className="font-display text-lg uppercase tracking-[0.14em] text-ice" data-id="element-1153" data-mp="3c0dbl04ydwib">Professional-Grade Lighting. Reimagined.</p>
          <h1 className="mt-2 font-display text-7xl font-semibold uppercase leading-[0.9] md:text-8xl" data-id="element-1154" data-mp="374o9ap1ju1ib">M1 PRO</h1>
          <p className="mt-2 text-xl text-white" data-id="element-1155" data-mp="29o8qej9co03j">Wireless RGB Lighting System</p>
          <p className="mt-6 max-w-lg text-lg text-titanium" data-id="element-1156" data-mp="29qd57w8sejil">
            A palm-sized, professional-grade light with 13 LED lenses in a white ABS housing. Rechargeable, wireless and
            magnetically mounted — no wires, no drilling, no complicated installation.
          </p>

          <div className="mt-8 border-y mf-hairline py-6" data-id="element-1157" data-mp="1m08y3ewe41mu">
            <p className="text-sm text-titanium" data-id="element-1158" data-mp="1pzri88ge08om">Starting at</p>
            <p className="text-3xl font-semibold" data-id="element-1159" data-mp="1pxwxov912il2">{formatMoney(pricingConfig.unitPrice, lang)}</p>
            <p className="mt-1 text-sm text-ice" data-id="element-1160" data-mp="1uxjcva8f28wi">
              As low as {formatMoney(minPer, lang)} per light in a Complete Lighting System
            </p>
            <div className="mt-4 space-y-1.5" data-id="element-1161" data-mp="129x9a1z6v49n">
              <WarrantyTag tier="oneYear" data-id="element-1162" data-mp="179s0k6mvlvv8" />
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-1163" data-mp="177ypmf58cphf">
            <Link to="/systems" className={`${buttonClasses('primary', 'lg')} flex-1`} data-id="element-1164" data-mp="zp7okgwnuvgn">
              Complete Systems
            </Link>
            <Link to="/build" className={`${buttonClasses('secondary', 'lg')} flex-1`} data-id="element-1165" data-mp="up346fsmtgl5">
              Build Your Own
            </Link>
          </div>

          <dl className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6" data-id="element-1166" data-mp="lr9e3mlgnj6f">
            {highlights.map(h => <div key={h.title} data-id="element-1167" data-mp="gsg6gasuume6">
                <dt className="font-display text-xl font-semibold uppercase" data-id="element-1168" data-mp="gthjulxcfkz2">{h.title}</dt>
                <dd className="mt-1 text-sm text-titanium" data-id="element-1169" data-mp="bzdaxx4ip2vc">{h.body}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section aria-labelledby="specs-title" className="border-t mf-hairline bg-navy" data-id="element-1170" data-mp="230j74sj8z38">
        <div className="mx-auto grid max-w-site gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-[1fr_1.5fr]" data-id="element-1171" data-mp="3u61gfo3hbs1j">
          <div data-id="element-1172" data-mp="3p6oj29ezf2kp">
            <h2 id="specs-title" className="font-display text-5xl font-semibold uppercase leading-[0.95]" data-id="element-1173" data-mp="3p89d7xxdwji1">
              Technical Specifications
            </h2>
            <p className="mt-5 max-w-md text-titanium" data-id="element-1174" data-mp="1x5nfkhvq9jcu">
              Manufacturer-advertised specifications. Actual run time depends on color, brightness mode and water
              temperature.
            </p>
          </div>
          <table className="w-full text-left" data-id="element-1175" data-mp="1ocjtxmaxi074">
            <caption className="sr-only" data-id="element-1176" data-mp="1ob8sht4yh4j6">M1 PRO technical specifications</caption>
            <tbody data-id="element-1177" data-mp="19ej67fk7euo2">
              {m1ProSpecs.map(s => <tr key={s.label} className="border-b mf-hairline" data-id="element-1178" data-mp="1efp0vixfcs08">
                  <th scope="row" className="py-4 pr-4 text-sm font-medium text-titanium" data-id="element-1179" data-mp="1ect3bvpho5cb">
                    {lang === 'es' ? s.labelEs : s.label}
                  </th>
                  <td className="py-4 text-white" data-id="element-1180" data-mp="15er51nx53qsq">
                    {s.value}
                    {s.status === 'supplier' && <PendingNote data-id="element-1181" data-mp="2urcfq7v7tr67">Supplier</PendingNote>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="pack-title" className="bg-deep" data-id="element-1182" data-mp="39wynwxyg0tj0">
        <div className="mx-auto grid max-w-site items-center gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-2" data-id="element-1183" data-mp="34y5a14f8v0ib">
          <ProductAssetSlot view="packaging" tone="studio" className="aspect-[4/3] w-full" data-id="element-1184" data-mp="3ipshm0emzvge" />
          <div data-id="element-1185" data-mp="3dxsfq2f0ash6">
            <h2 id="pack-title" className="font-display text-5xl font-semibold uppercase leading-[0.95]" data-id="element-1186" data-mp="3dx8vug5spu0i">
              Packaged Like Professional Equipment.
            </h2>
            <p className="mt-5 max-w-lg text-titanium" data-id="element-1187" data-mp="3non0f2cltk0i">
              Each M1 PRO arrives in MarineForceLED's premium presentation box — protected for transport and presented the way
              professional-grade equipment should be.
            </p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2" data-id="element-1188" data-mp="1eizgidb70j7">
              <div data-id="element-1189" data-mp="27adba60jfr8m">
                <h3 className="text-sm font-semibold text-white" data-id="element-1190" data-mp="279vjblkwrz6i">Box design</h3>
                <ul className="mt-3 space-y-2 text-sm text-titanium" data-id="element-1191" data-mp="239bl0eolmpis">
                  {packagingDesign.map(d => <li key={d} data-id="element-1192" data-mp="6970s5g05bn4">{d}</li>)}
                </ul>
              </div>
              <div data-id="element-1193" data-mp="b6pd85t8a8zv">
                <h3 className="text-sm font-semibold text-white" data-id="element-1194" data-mp="b8a7dubmrpx7">In the box</h3>
                <ul className="mt-3 space-y-2 text-sm text-titanium" data-id="element-1195" data-mp="g48ulel831w1">
                  {packagingContents.map(c => <li key={c.item} data-id="element-1196" data-mp="p68ag3ki3zxe">
                      {c.item}
                      {c.status === 'supplier-pending' && <PendingNote data-id="element-1197" data-mp="u5bcrg93js1o">Supplier TBC</PendingNote>}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}